import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, setFontSize, uploadMedia, dataUrlToBase64, compressImage, mediaSrcById } from "../api";
import { galleryAvailable, saveToGallery } from "../lib/gallery";
import { BuddyMeasureCard } from "./BuddyMeasureCard";
import { BuddyTrimCard } from "./BuddyTrimCard";
import { setTheme } from "../lib/theme";
import { saveOfflineSettings, clearOfflineData } from "../lib/offlineSync";
import { InviteDialog } from "./InviteDialog";
import { ChatterExpressDialog } from "./ChatterExpressDialog";
import { getPosition, warmPosition, cachedPosition, openExternal } from "../lib/geo";
import { useApp } from "../context/AppContext";
import { warmMic, releaseMic } from "../lib/mic";
import { captureUtterance, chime, pickUtteranceMime } from "../lib/vad";
import { createWakeListener, wakeWordSupported, bargeInCapable } from "../lib/wakeword";
import { Capacitor } from "@capacitor/core";
import { BuddySpeakButton } from "./MessageBubble";
import { ReminderAlarm } from "./ReminderAlarm";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { X, Mic, Send, Ear, EarOff, Volume2, VolumeX, Hammer, Brain, Pencil, Trash2, Plus, Check, Languages, Minus,
} from "lucide-react";
import { toast } from "sonner";
import { InterpreterMode } from "./InterpreterMode";
import { soundHammer, soundDrill, soundSwoosh, setSfxVolume, playVoice, unlockAudio, primeVoicePlayback, BUDDY_SPEEDS, getBuddySpeed, setBuddySpeed } from "../lib/sounds";
import buddyBody from "../assets/buddy-body.webp";
import buddyHead from "../assets/buddy-head.webp";
import buddyPeek from "../assets/buddy-peek.png";
import buddyPeekHands from "../assets/buddy-peek-hands.png";
import buddyPeekBlink from "../assets/buddy-peek-blink.png";
import buddyExit from "../assets/buddy-exit.webp";
import buddyReturn from "../assets/buddy-return.webp";
const RETURN_MS = 2450;          // buddy-return.webp: 49 frames × 50ms, plays once
const RET_BADGE_FRAC = 0.73, RET_BADGE_CX = 0.49, RET_BADGE_CY = 0.49;
const EXIT_MS = 3600;            // buddy-exit.webp: 52 frames × 50ms (2.6s) + 1s hold on the last frame, then fade
const EXIT_BADGE_FRAC = 0.83;    // badge diameter ≈ 83% of the animation frame
const EXIT_BADGE_CX = 0.554, EXIT_BADGE_CY = 0.514; // badge centre inside the frame
import buddyArm from "../assets/buddy-arm-wave.webp";
import buddyTools from "../assets/buddy-tools.webp";

/**
 * BUDDY — the animated AI field helper.
 * A Disney-style cartoon face (yellow builder hard hat) floating above every
 * screen. Voice-only input (hold to talk), replies with text in a speech
 * balloon AND spoken aloud (TTS). Real actions with 1-tap confirmation
 * (message drafts) and reminders delivered as push notifications.
 */

const SIZES = { p: 84, m: 107, g: 132 }; // +44% over the original (two +20% steps, user request) // Buddy on-screen size: pequeno | médio | grande
const SIZE_ORDER = ["p", "m", "g"];
const SFX_LEVELS = { on: 1, low: 0.35, off: 0 }; // tool-sound volume
const SFX_ORDER = ["on", "low", "off"];
const EDGE = 10;

// Voice confirmation of pending drafts — FULL-utterance match only, so a new
// command like "manda mensagem pro João..." never triggers a false confirm.
const CONFIRM_RE = /^(sim|ok|okay|yes|beleza|isso|pronto|claro|pode|quero|confirma(r|do)?|pode\s+(enviar|mandar|confirmar|salvar|lembrar|guardar)|lembra(r)?|salva(r)?|guarda(r)?|anota(r)?|envia(r)?|manda(r)?|send(\s+it)?|confirm|remember(\s+it)?|save(\s+it)?|go\s+ahead|yes\s+send)[\s.!]*$/i;
const CANCEL_RE = /^(n[aã]o|cancela(r)?|n[aã]o\s+(envia|enviar|manda|mandar)|deixa|esquece|cancel|no|don'?t\s+send|nevermind|never\s+mind)[\s.!]*$/i;

// End the hands-free conversation by voice: "obrigado Buddy", "tchau", "valeu"...
// FULL-utterance match only — "obrigado, agora manda pro João" keeps the dialogue going.
const END_RE = /^(?:(?:muito\s+)?(?:obrigad[oa]o?|brigad[oa]o?|valeu|thanks?|thank\s+you)(?:\s+buddy)?(?:[\s,.!]+(?:tchau|xau|at[eé]\s+(?:mais|logo)|bye))?|(?:tchau|xau|falou|at[eé]\s+(?:mais|logo|amanh[aã])|bye|goodbye|good\s*night|boa\s+noite|see\s+you|[eé]\s+s[oó](?:\s+isso)?|s[oó]\s+isso|that'?s\s+(?:all|it))(?:[\s,]+buddy)?)[\s.!]*$/i;
// Strong goodbye words: end the conversation even inside a short mixed sentence
// ("Tchau Buddy, obrigado!" / "Valeu, até mais!") — long sentences keep the flow.
// NOTE: "falou"/"boa noite" stay full-utterance only (END_RE) — they appear in normal questions.
const BYE_WORDS_RE = /\b(tchau+|xau+|chau|ciao|adeus|bye|goodbye|ate (mais|logo|amanha|a proxima)|see you)\b/i;
// Thanks-only sentences also close the chat: "obrigado pela sua ajuda", "valeu por tudo"
const BYE_THANKS_RE = /^(muito\s+|mto\s+)?(obrigad[oa]s?|brigad[oa]o?|valeu|thanks|thank\s+you)(\s+(buddy|amigo|cara|man))?(\s+(pela|pelas|pelo|pelos|por|for)\s+\w+(\s+\w+){0,3})?\s*$/i;
const normText = (s) => (s || "")
  .toLowerCase()
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "") // strip accents
  .replace(/[^a-z0-9\s']/g, " ").replace(/\s+/g, " ").trim();
const isFarewell = (s) => {
  const norm = normText(s);
  if (!norm) return false;
  if (END_RE.test(norm)) return true; // exact short farewells ("obrigado", "valeu"...)
  const n = norm.split(" ").length;
  if (n <= 8 && BYE_WORDS_RE.test(norm)) return true;
  return n <= 7 && BYE_THANKS_RE.test(norm);
};
// Trailing thanks ("...eu so queria testar, obrigado!") closes the chat too —
// people naturally end conversations this way even in longer sentences.
const TRAILING_THANKS_RE = /\b(obrigad[oa]s?|brigad[oa]o?|valeu|thanks|thank you)(\s+(buddy|amigo|cara|man))?\s*$/;
// Buddy courtesy closers ("De nada! ...") — when HE closes, stop listening too.
const REPLY_CLOSER_RE = /^(de nada|por nada|disponha|imagina|magina|as ordens|sempre as ordens|you'?re welcome|no problem|anytime|my pleasure)\b/;
// "ok, obrigado" / "beleza" / "tá bom, valeu": pure acknowledgement (≤ 6 words) → the conversation is over
const ACK_CORE = new Set(["ok", "okay", "okey", "beleza", "blz", "certo", "entendi", "entendido", "perfeito", "show", "legal", "joia", "otimo", "massa", "fechou", "combinado", "valeu", "obrigado", "obrigada", "obrigados", "brigado", "brigada", "thanks", "thank", "alright", "fine", "cool", "great", "perfect", "understood", "gracias", "vale", "listo", "perfecto", "bueno", "isso", "got", "tranquilo", "suave", "bom", "boa", "ta", "tah"]);
const ACK_FILLER = new Set(["muito", "buddy", "amigo", "cara", "man", "tudo", "bem", "e", "so", "then", "entao", "you", "it", "all", "right", "nada", "mais", "that", "s", "por", "agora", "ai", "hoje"]);
const isAckCloser = (s) => {
  const w = normText(s).split(" ").filter(Boolean);
  if (!w.length || w.length > 6) return false;
  return w.every((x) => ACK_CORE.has(x) || ACK_FILLER.has(x)) && w.some((x) => ACK_CORE.has(x));
};

const clampPos = (x, y, b = 74) => ({
  x: Math.min(Math.max(x, EDGE), window.innerWidth - b - EDGE),
  y: Math.min(Math.max(y, EDGE + 40), window.innerHeight - b - EDGE - 60),
});

// Local time WITH offset (so Buddy resolves "in 15 min" / "at 3pm" correctly)
const localISO = () => {
  const d = new Date();
  const off = -d.getTimezoneOffset();
  const sign = off >= 0 ? "+" : "-";
  const p = (n) => String(Math.floor(Math.abs(n))).padStart(2, "0");
  return (
    new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 19) +
    `${sign}${p(off / 60)}:${p(off % 60)}`
  );
};

/** Buddy — the user's cartoon builder artwork, cut into a live puppet rig.
 * Layers (bottom -> top): body (badge reconstructed behind the holes),
 * tools (drill hand), head (rotates at the neck; carries eyelids + talking
 * mouth window), waving arm (rotates at the shoulder, in front like the art).
 * Every mood animates the whole body naturally: waving, head sway, breathing,
 * blinking and seamless mouth motion.
 * mood: idle | listening | thinking | talking
 */
/** Hidden Buddy: the artwork of him gripping a wall edge, parked behind the
 *  screen edge where he was dragged out. Idle → only his FINGERS show.
 *  Every 30s: hands come over the edge, he slides in until the full picture
 *  shows, blinks, stays ~3s and slides back out. Tap = bring him back. */
const PEEK_HAND = 0.26;            // x-fraction of the artwork taken by the hands (wrist → fingertips)
const PEEK_RATIO = 869 / 480;      // artwork height / width
const PEEK_INTERVAL_MS = 30000;        // normal cadence
const PEEK_INTERVAL_UNREAD_MS = 15000; // something unread → peeks twice as often
const PEEK_INTERVAL_IDLE_MS = 90000;   // screen untouched for a while → calmer
const PEEK_IDLE_AFTER_MS = 120000;
const PEEK_EVENT_COOLDOWN_MS = 8000;   // min gap between event-triggered peeks
const PEEK_TOTAL_MS = 700 + 900 + 3000;
/* Artwork: head on the left, hands on the right (fingertips at the right end).
 * The container sits flush with the screen edge and is MIRRORED on the left
 * edge, so the hands always hug the edge and the head comes INTO the screen.
 * Smart cadence: unread → more often; idle screen → less; a new message or
 * someone typing to you → peeks right away. */
const BuddyPeek = ({ pos, size, onTap, onDragY }) => {
  const [phase, setPhase] = useState("idle"); // idle | hands | in
  const unreadRef = useRef(typeof window !== "undefined" ? (window.__oscUnread || 0) : 0);
  const lastTouchRef = useRef(Date.now());
  const lastPeekRef = useRef(0);
  useEffect(() => {
    const timers = [];
    let next;
    const nextDelay = () => {
      if (unreadRef.current > 0) return PEEK_INTERVAL_UNREAD_MS;
      if (Date.now() - lastTouchRef.current > PEEK_IDLE_AFTER_MS) return PEEK_INTERVAL_IDLE_MS;
      return PEEK_INTERVAL_MS;
    };
    const schedule = () => { clearTimeout(next); next = setTimeout(run, nextDelay()); };
    const run = () => {
      clearTimeout(next);
      lastPeekRef.current = Date.now();
      setPhase("hands");
      timers.push(setTimeout(() => setPhase("in"), 700));
      timers.push(setTimeout(() => { setPhase("idle"); schedule(); }, PEEK_TOTAL_MS));
    };
    const peekNow = () => {
      if (Date.now() - lastPeekRef.current < Math.max(PEEK_EVENT_COOLDOWN_MS, PEEK_TOTAL_MS)) return;
      run();
    };
    const onUnread = (e) => { unreadRef.current = e.detail?.total || 0; };
    const onNotify = (e) => {
      const p = e.detail || {};
      if (p.inner === "typing") peekNow();
      else if (p.inner === "message" && p.sender_id && !p.muted) peekNow();
    };
    const onTouch = () => { lastTouchRef.current = Date.now(); };
    window.addEventListener("osc-unread-count", onUnread);
    window.addEventListener("osc-user-notify", onNotify);
    ["pointerdown", "keydown", "wheel", "touchstart"].forEach((ev) => window.addEventListener(ev, onTouch, { passive: true }));
    schedule();
    return () => {
      clearTimeout(next); timers.forEach(clearTimeout);
      window.removeEventListener("osc-unread-count", onUnread);
      window.removeEventListener("osc-user-notify", onNotify);
      ["pointerdown", "keydown", "wheel", "touchstart"].forEach((ev) => window.removeEventListener(ev, onTouch));
    };
  }, []);
  const vw = typeof window !== "undefined" ? window.innerWidth : 390;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const right = !pos || (pos.x + size / 2) > vw / 2;
  // head in the artwork ≈ 1.1×W tall → match the floating Buddy button size
  const W = Math.round(size * 0.76);
  const H = Math.round(W * PEEK_RATIO);
  const top = Math.max(50, Math.min((pos?.y ?? 200) + Math.round(size / 2) - Math.round(H * 0.32), vh - H - 90));
  // hands layer is flipped so the FINGERTIPS point into the screen; idle shows only them
  const handsShift = phase === "in" ? W : phase === "hands" ? Math.round(W * (1 - PEEK_HAND - 0.02)) : Math.round(W * (1 - 0.11));
  const fullShift = phase === "in" ? 0 : W;
  const fullDur = phase === "in" ? ".9s" : ".7s";
  // vertical drag of the fingers (same edge only): move = slide up/down, no move = tap
  const dragRef = useRef(null);
  const onDown = (e) => {
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (err) { /* pointer already gone */ }
    dragRef.current = { py: e.clientY, y: pos?.y ?? 200, moved: false };
  };
  const onMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dy = e.clientY - d.py;
    if (!d.moved && Math.abs(dy) > 6) d.moved = true;
    if (d.moved) onDragY?.(d.y + dy, false);
  };
  const onUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    if (!d) return;
    if (d.moved) onDragY?.(d.y + (e.clientY - d.py), true);
    else onTap?.();
  };
  return (
    <div
      data-testid="buddy-peek"
      data-phase={phase}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      onContextMenu={(e) => e.preventDefault()}
      style={{ top, [right ? "right" : "left"]: 0, width: W, height: H, transform: right ? undefined : "scaleX(-1)", touchAction: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none" }}
      className="fixed z-[45] cursor-grab active:cursor-grabbing overflow-hidden select-none"
    >
      {/* hands gripping the edge (idle → fingers only; "hands" → whole hands) */}
      <img
        src={buddyPeekHands} alt="" draggable={false}
        className="absolute inset-0 block w-full h-full pointer-events-none buddy-peek-img"
        style={{ transform: `translateX(${handsShift}px) scaleX(-1)`, opacity: phase === "in" ? 0 : 1,
          transition: `transform ${phase === "hands" ? ".5s" : ".6s"} cubic-bezier(.3,.9,.3,1), opacity .35s ${phase === "in" ? ".35s" : ".3s"}` }}
      />
      {/* full picture slides in from behind the edge, blinks, slides back */}
      <div
        className="absolute inset-0 buddy-peek-img"
        style={{ transform: `translateX(${fullShift}px)`, transition: `transform ${fullDur} cubic-bezier(.3,.9,.3,1)` }}
      >
        <img src={buddyPeek} alt="" draggable={false} className="block w-full h-full pointer-events-none" />
        {/* his eyes-closed picture fades over the open-eyed one → blink */}
        <img src={buddyPeekBlink} alt="" draggable={false} className={`absolute inset-0 block w-full h-full pointer-events-none opacity-0 ${phase === "in" ? "buddy-peek-blink" : ""}`} />
      </div>
    </div>
  );
};

export const BuddyFace = ({ mood = "idle", size = 56, greet = false }) => (
  <svg
    viewBox="0 0 640 640"
    width={size}
    height={size}
    className="buddy-face"
    data-mood={mood}
    data-greet={greet ? "1" : undefined}
    aria-hidden="true"
  >
    <defs>
      <linearGradient id="buddyLidL" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#EF9A55" />
        <stop offset="100%" stopColor="#D06C2E" />
      </linearGradient>
      <linearGradient id="buddyLidR" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#F7AA62" />
        <stop offset="100%" stopColor="#E08440" />
      </linearGradient>
      <filter id="buddyLidSoft" x="-30%" y="-30%" width="160%" height="160%">
        <feGaussianBlur stdDeviation="1.6" />
      </filter>
      <radialGradient id="buddyMouthFade">
        <stop offset="62%" stopColor="#fff" stopOpacity="1" />
        <stop offset="100%" stopColor="#fff" stopOpacity="0" />
      </radialGradient>
      <mask id="buddyMouthMask">
        <ellipse cx="310" cy="277" rx="78" ry="52" fill="url(#buddyMouthFade)" transform="rotate(-14 310 277)" />
      </mask>
    </defs>

    <g className="buddy-breathe">
      {/* torso + vest (badge reconstructed behind the moving parts) */}
      <image className="buddy-body" href={buddyBody} x="0" y="0" width="640" height="640" />

      {/* drill / hammer hand */}
      <image className="buddy-tools" href={buddyTools} x="0" y="0" width="640" height="640" />

      {/* head: rotates at the neck; lids + mouth ride along */}
      <g className="buddy-head-g">
        <image href={buddyHead} x="0" y="0" width="640" height="640" />
        {/* talking: mouth pixels breathe inside a fixed soft window */}
        <g mask="url(#buddyMouthMask)">
          <image className="buddy-talk-patch" href={buddyHead} x="0" y="0" width="640" height="640" />
        </g>
        {/* eyelids — rotated to the head tilt; scaleY animates the blink */}
        <g className="buddy-lids" filter="url(#buddyLidSoft)">
          <g transform="rotate(-21 262 224)">
            <ellipse className="buddy-lid" cx="262" cy="224" rx="27" ry="28" fill="url(#buddyLidL)" />
          </g>
          <g transform="rotate(-21 336 193)">
            <ellipse className="buddy-lid" cx="336" cy="193" rx="27" ry="29" fill="url(#buddyLidR)" />
          </g>
        </g>
      </g>

      {/* waving arm — in front, rotates at the shoulder */}
      <image className="buddy-arm" href={buddyArm} x="0" y="0" width="640" height="640" />
    </g>
  </svg>
);

export const BuddyAssistant = () => {
  const { user, t } = useApp();
  const navigate = useNavigate();
  const [pos, setPos] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [open, setOpen] = useState(false);
  // HIDDEN mode: Buddy dragged off-screen (or "–" button); "Hey Buddy" keeps
  // listening and brings him back; every 30s he peeks from the edge.
  const [hidden, setHidden] = useState(false);
  const [flashVisible, setFlashVisible] = useState(false); // temporary reveal (reminder/alarm)
  const flashTimerRef = useRef(null);
  const [mood, setMood] = useState("idle"); // idle | listening | thinking | talking
  const [log, setLog] = useState([]); // {role: user|buddy, text}
  const [draft, setDraft] = useState(null); // generic draft action
  const [measure, setMeasure] = useState(null); // voice-guided measurement session (live card)
  const measureRef = useRef(null);
  useEffect(() => { measureRef.current = measure && measure.status === "active" ? measure : null; }, [measure]);
  const trimRef = useRef(null);
  const [measureBusy, setMeasureBusy] = useState(false);
  const [trim, setTrim] = useState(null); // voice trim-notes session (live card)
  const [trimBusy, setTrimBusy] = useState(false);
  useEffect(() => { trimRef.current = trim && trim.status === "active" ? trim : null; }, [trim]);
  const measurePhotoRef = useRef(null); // hidden <input type=file> fallback for the unit photo
  const measurePhotoTargetRef = useRef(null); // {sessionId, n}
  const [inviteTarget, setInviteTarget] = useState(null); // invite_person -> InviteDialog
  const [chatterTarget, setChatterTarget] = useState(null); // chatter_report -> ChatterExpressDialog
  const [sendingDraft, setSendingDraft] = useState(false);
  const [wakeEnabled, setWakeEnabled] = useState(false);
  const [wakeAwake, setWakeAwake] = useState(false); // heard "Hey Buddy", waiting for the command
  const [handsFree, setHandsFree] = useState("off"); // off | waiting | recording (VAD session)
  const [greet, setGreet] = useState(false); // one-shot welcome wave
  const [voiceOn, setVoiceOn] = useState(() => {
    try { return localStorage.getItem("osc_buddy_voice") !== "0"; } catch (e) { return true; }
  }); // speaker: default ON (Buddy speaks); muted = text only
  const [sizeKey, setSizeKey] = useState(() => {
    try { return SIZES[localStorage.getItem("osc_buddy_size_v2")] ? localStorage.getItem("osc_buddy_size_v2") : "g"; } catch (e) { return "g"; }
  });
  const btn = SIZES[sizeKey] || SIZES.g;
  const [speed, setSpeed] = useState(() => getBuddySpeed());
  const cycleSpeed = () => {
    setSpeed((v) => {
      const next = BUDDY_SPEEDS[(BUDDY_SPEEDS.indexOf(v) + 1) % BUDDY_SPEEDS.length];
      setBuddySpeed(next);
      return next;
    });
  };
  const [sfxKey, setSfxKey] = useState(() => {
    try { return SFX_LEVELS[localStorage.getItem("osc_buddy_sfx")] !== undefined ? localStorage.getItem("osc_buddy_sfx") : "on"; } catch (e) { return "on"; }
  });
  const [histLoading, setHistLoading] = useState(false);
  const [histHasMore, setHistHasMore] = useState(false);
  const [interpOpen, setInterpOpen] = useState(false); // live interpreter (client mode)
  const [typedCmd, setTypedCmd] = useState(""); // typed command in the balloon

  // Interpreter open: silence EVERYTHING of Buddy's (wake word, hands-free,
  // speech) — the interpreter owns the mic while it is on screen.
  const openInterpreter = () => {
    // unlock streamed TTS playback while we're still inside the user gesture
    try { primeVoicePlayback(); } catch (e) { /* ignore */ }
    try { unlockAudio(); } catch (e) { /* ignore */ }
    try { hfRef.current?.cancel?.(); } catch (e) { /* ignore */ }
    hfRef.current = null;
    try { audioRef.current?.pause?.(); } catch (e) { /* ignore */ }
    audioRef.current = null;
    try { wakeRef.current?.suspend?.(); } catch (e) { /* ignore */ }
    releaseMic();
    setHandsFree("off");
    setMood("idle");
    setOpen(false);
    setInterpOpen(true);
  };
  const closeInterpreter = () => {
    setInterpOpen(false);
    try { wakeRef.current?.resume?.(); } catch (e) { /* ignore */ }
  };

  // Other screens (e.g. the Messages FAB) can open the interpreter via a
  // window event — keeps a single owner for mic/wake coordination.
  const openInterpRef = useRef(null);
  openInterpRef.current = openInterpreter;
  useEffect(() => {
    const h = () => openInterpRef.current?.();
    window.addEventListener("open-interpreter", h);
    return () => window.removeEventListener("open-interpreter", h);
  }, []);
  const histLoadedRef = useRef(false);
  const prependingRef = useRef(false);
  const voiceOnRef = useRef(true);
  const greetTimerRef = useRef(null);
  const triggerGreetRef = useRef(null);
  const wakeRef = useRef(null);
  const micPrimedRef = useRef(false);
  const moodRef = useRef("idle");
  const openRef = useRef(false);
  const hfRef = useRef(null); // active captureUtterance session
  const startHandsFreeRef = useRef(null);
  const runAskRef = useRef(null);
  const draftRef = useRef(null);
  const sendingRef = useRef(false);
  const dragRef = useRef(null);
  const recRef = useRef(null); // {recorder, chunks, startedAt, stream}
  const audioRef = useRef(null);
  const speakingTextRef = useRef(""); // text Buddy is speaking right now (barge-in echo guard)
  const logEndRef = useRef(null);
  const posKey = user ? `osc_buddy_pos_v1_${user.id}` : null;

  // ---------- position ----------
  useEffect(() => {
    if (!posKey) return;
    let start = { x: EDGE, y: Math.round(window.innerHeight * 0.42) };
    try {
      const saved = JSON.parse(localStorage.getItem(posKey) || "null");
      if (saved && Number.isFinite(saved.x) && Number.isFinite(saved.y)) start = saved;
    } catch (e) { /* ignore */ }
    setPos(clampPos(start.x, start.y, btn));
    // Re-clamp on real viewport changes (rotation) — NOT when the mobile keyboard
    // shrinks the webview: Buddy floats, he must stay where he was.
    const isEditable = (el) => !!el && (el.tagName === "TEXTAREA" || el.tagName === "INPUT" || el.isContentEditable);
    let lastH = window.innerHeight;
    const onResize = () => {
      const h = window.innerHeight;
      const keyboard = isEditable(document.activeElement) && h < lastH;
      if (!isEditable(document.activeElement)) lastH = h;
      if (keyboard) return;
      setPos((p) => (p ? clampPos(p.x, p.y, btn) : p));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [posKey, btn]);

  useEffect(() => { voiceOnRef.current = voiceOn; }, [voiceOn]);

  // unlock the audio engine on any user gesture (desktop autoplay policies).
  // NOT on the native app: Capacitor plays without a gesture, and creating an
  // AudioContext on the very first tap reconfigures the iOS audio session under
  // the running "Hey Buddy" recognizer — that first tap then does nothing.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return undefined;
    const unlock = () => unlockAudio();
    window.addEventListener("pointerdown", unlock, { passive: true });
    return () => window.removeEventListener("pointerdown", unlock);
  }, []);

  const toggleVoice = () => {
    setVoiceOn((v) => {
      const next = !v;
      try { localStorage.setItem("osc_buddy_voice", next ? "1" : "0"); } catch (e) { /* ignore */ }
      if (!next && audioRef.current) {
        try { audioRef.current.pause(); } catch (e) { /* ignore */ }
        speakingTextRef.current = "";
        if (moodRef.current === "talking") { setMood("idle"); moodRef.current = "idle"; }
      }
      return next;
    });
  };

  const cycleSize = () => {
    setSizeKey((k) => {
      const next = SIZE_ORDER[(SIZE_ORDER.indexOf(k) + 1) % SIZE_ORDER.length];
      try { localStorage.setItem("osc_buddy_size_v2", next); } catch (e) { /* ignore */ }
      return next;
    });
  };

  // tool-sound volume: on -> low -> off
  useEffect(() => { setSfxVolume(SFX_LEVELS[sfxKey] ?? 1); }, [sfxKey]);
  const cycleSfx = () => {
    setSfxKey((k) => {
      const next = SFX_ORDER[(SFX_ORDER.indexOf(k) + 1) % SFX_ORDER.length];
      try { localStorage.setItem("osc_buddy_sfx", next); } catch (e) { /* ignore */ }
      setSfxVolume(SFX_LEVELS[next]);
      if (next !== "off") soundHammer(); // little preview so the user hears the level
      return next;
    });
  };

  // ---------- Buddy memories (review / add / edit / delete) ----------
  const [view, setView] = useState("chat"); // chat | memories
  const [memories, setMemories] = useState([]);
  const [memLoading, setMemLoading] = useState(false);
  const [memBusy, setMemBusy] = useState(false);
  const [memNew, setMemNew] = useState("");
  const [memEditId, setMemEditId] = useState(null);
  const [memEditText, setMemEditText] = useState("");

  // ---------- Buddy personality (per-user style + free instructions) ----------
  const [memStyle, setMemStyle] = useState("default");
  const [memInstr, setMemInstr] = useState("");
  const [prefsBusy, setPrefsBusy] = useState(false);

  const savePrefs = async (styleOverride = null) => {
    if (prefsBusy) return;
    setPrefsBusy(true);
    try {
      const style = styleOverride || memStyle;
      await api.put("/buddy/settings", { user_id: user.id, style, instructions: memInstr });
      toast.success(t("buddy_prefs_saved"));
    } catch (e) { toast.error(t("buddy_error")); } finally {
      setPrefsBusy(false);
    }
  };

  const loadMemories = async () => {
    if (!user) return;
    setMemLoading(true);
    try {
      const [r, rs] = await Promise.all([
        api.get("/buddy/memories", { params: { user_id: user.id } }),
        api.get("/buddy/settings", { params: { user_id: user.id } }),
      ]);
      setMemories(r.data?.memories || []);
      setMemStyle(rs.data?.style || "default");
      setMemInstr(rs.data?.instructions || "");
    } catch (e) { /* ignore */ } finally {
      setMemLoading(false);
    }
  };

  const toggleMemoriesView = () => {
    setView((v) => {
      const next = v === "memories" ? "chat" : "memories";
      if (next === "memories") loadMemories();
      return next;
    });
  };

  const addMemory = async () => {
    const text = memNew.trim();
    if (!text || memBusy) return;
    setMemBusy(true);
    try {
      const r = await api.post("/buddy/memories", { user_id: user.id, text });
      setMemories((prev) => [r.data, ...prev]);
      setMemNew("");
      toast.success(t("buddy_memory_saved"));
    } catch (e) { toast.error(t("buddy_error")); } finally {
      setMemBusy(false);
    }
  };

  const saveMemoryEdit = async () => {
    const text = memEditText.trim();
    if (!text || memBusy) return;
    setMemBusy(true);
    try {
      await api.put(`/buddy/memories/${memEditId}`, { user_id: user.id, text });
      setMemories((prev) => prev.map((m) => (m.id === memEditId ? { ...m, text } : m)));
      setMemEditId(null);
      setMemEditText("");
      toast.success(t("buddy_memory_updated"));
    } catch (e) { toast.error(t("buddy_error")); } finally {
      setMemBusy(false);
    }
  };

  const deleteMemory = async (id) => {
    if (memBusy) return;
    setMemBusy(true);
    try {
      await api.delete(`/buddy/memories/${id}`, { params: { user_id: user.id } });
      setMemories((prev) => prev.filter((m) => m.id !== id));
      toast.success(t("buddy_memory_deleted"));
    } catch (e) { toast.error(t("buddy_error")); } finally {
      setMemBusy(false);
    }
  };

  // ---------- persistent conversation history ----------
  const loadHistory = async (before = "") => {
    if (!user || histLoading) return;
    setHistLoading(true);
    try {
      const r = await api.get("/buddy/history", { params: { user_id: user.id, before, limit: 30 } });
      const rows = (r.data?.messages || []).slice().reverse(); // oldest first
      const mapped = rows.map((m) => ({
        role: m.role === "buddy" ? "buddy" : "user",
        text: m.text,
        ts: m.created_at,
      }));
      setHistHasMore(!!r.data?.has_more);
      if (mapped.length) {
        prependingRef.current = !!before;
        setLog((prev) => [...mapped, ...prev]);
      }
    } catch (e) { /* ignore */ } finally {
      setHistLoading(false);
    }
  };

  useEffect(() => {
    if (open && user && !histLoadedRef.current) {
      histLoadedRef.current = true;
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id]);

  // ---------- reminder alarm (app open) — handled by <ReminderAlarm /> ----------

  // auto-scroll the log (skip when prepending older history)
  useEffect(() => {
    if (prependingRef.current) { prependingRef.current = false; return; }
    logEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [log, draft]);

  // ---------- "Hey Buddy" wake word (foreground, hands-free) ----------
  const wakeKey = user ? `osc_buddy_wake_v1_${user.id}` : null;

  useEffect(() => { moodRef.current = mood; }, [mood]);
  useEffect(() => { draftRef.current = draft; }, [draft]);
  useEffect(() => { openRef.current = open; }, [open]);
  useEffect(() => {
    if (!open || !user?.id) return;
    api.get("/buddy/measure-session", { params: { user_id: user.id } })
      .then((r) => { if (r.data?.session) setMeasure(r.data.session); })
      .catch(() => {});
    api.get("/buddy/trim-session", { params: { user_id: user.id } })
      .then((r) => { if (r.data?.session) setTrim(r.data.session); })
      .catch(() => {});
  }, [open, user?.id]);

  // cancel any hands-free session on unmount + free the shared mic stream
  useEffect(() => () => {
    try { hfRef.current?.cancel(); } catch (e) { /* ignore */ }
    releaseMic();
  }, []);

  // one-shot cheerful welcome wave (on tap or "Hey Buddy")
  const triggerGreet = () => {
    setGreet(false);
    clearTimeout(greetTimerRef.current);
    // restart the CSS animation even if a greet is already running
    requestAnimationFrame(() => {
      setGreet(true);
      greetTimerRef.current = setTimeout(() => setGreet(false), 1700);
    });
  };
  useEffect(() => () => clearTimeout(greetTimerRef.current), []);
  triggerGreetRef.current = triggerGreet;

  // ---------- hands-free voice session (VAD) ----------
  // After "Hey Buddy" the user doesn't hold any button: we wait for speech,
  // record while they talk and stop automatically when they pause. After the
  // reply we listen again so the dialogue flows naturally.
  const cancelHandsFree = () => {
    const s = hfRef.current;
    hfRef.current = null;
    try { s?.cancel(); } catch (e) { /* ignore */ }
    releaseMic(); // IMMEDIATE — never leave the mic open while wake may resume (iOS crash)
    setHandsFree("off");
  };

  // barge-in: cut Buddy's speech immediately (pause() never fires onEnd,
  // so the old reply's follow-up loop is skipped and the new session takes over)
  const stopSpeaking = () => {
    try { audioRef.current?.pause?.(); } catch (e) { /* ignore */ }
    audioRef.current = null;
    speakingTextRef.current = "";
  };

  const wakePrefixRef = useRef(""); // words the wake-word recognizer heard right after "Hey Buddy"
  const startHandsFree = async (followUp = false) => {
    if (recRef.current || hfRef.current) return;
    // block only NEW sessions while a request is in flight — follow-ups arrive
    // right after the reply, when the mood may still read "thinking"
    if (!followUp && moodRef.current === "thinking") return;
    try { wakeRef.current?.suspend(); } catch (e) { /* ignore */ }
    setOpen(true);
    setWakeAwake(false);
    if (!followUp) triggerGreet(); // cheerful hello on "Hey Buddy"
    setMood("thinking"); // mic opening — the RED light only comes on when audio is really being captured
    const session = captureUtterance({
      waitForSpeechMs: followUp ? 7000 : 9000,
      // natural mid-sentence pauses must not cut the phrase; dictating measurements pauses even more
      silenceMs: (measureRef.current || trimRef.current) ? 1600 : 1000,
      maxUtteranceMs: 30000,
      // after Buddy's own question there is no wake word to skip: listen from the very first ms
      armDelayMs: followUp ? 0 : 250,
      onReady: () => { setMood("listening"); setHandsFree("waiting"); chime("start"); },
      onSpeechStart: () => setHandsFree("recording"),
    });
    hfRef.current = session;
    let result = null;
    try { result = await session.promise; } catch (e) { result = null; }
    if (hfRef.current !== session) return; // cancelled / replaced by hold-to-talk
    hfRef.current = null;
    setHandsFree("off");
    if (result) {
      chime("end");
      const prefix = followUp ? "" : wakePrefixRef.current;
      wakePrefixRef.current = "";
      await runAskRef.current?.({ blob: result.blob, filename: result.filename, handsFree: true, prefix });
    } else {
      releaseMic(); // IMMEDIATE — wake resumes on "idle"; open mic + recognizer start crashes iOS
      setMood("idle"); // silence — back to passive "Hey Buddy" listening
    }
  };
  startHandsFreeRef.current = startHandsFree;

  // restore preference (default ON when the platform supports it)
  useEffect(() => {
    if (!wakeKey || !wakeWordSupported()) return;
    let pref = "1";
    try { pref = localStorage.getItem(wakeKey) ?? "1"; } catch (e) { /* ignore */ }
    setWakeEnabled(pref === "1");
  }, [wakeKey]);

  const hiddenKey = user ? `buddy_hidden_${user.id}` : null;
  useEffect(() => {
    if (!hiddenKey) return;
    try { setHidden(localStorage.getItem(hiddenKey) === "1"); } catch (e) { /* ignore */ }
  }, [hiddenKey]);
  // slide state: "" | "out" (going to the edge) | "in" (coming back from the edge)
  const [slide, setSlide] = useState("");
  const [noBob, setNoBob] = useState(false); // keep the bob animation off while sliding in
  const edgeShift = () => {
    const vw = window.innerWidth;
    const toRight = !pos || (pos.x + btn / 2) > vw / 2;
    return toRight ? (vw - (pos?.x ?? 0)) + 8 : -((pos?.x ?? 0) + btn + 8);
  };
  // exit animation: Buddy hops out of his badge and runs off toward the edge he hides at
  const [exiting, setExiting] = useState(null); // null | { toRight: bool, key, x, y }
  const [fabGone, setFabGone] = useState(false); // FAB crossfades into the animation's first frame
  const hideBuddy = (at) => {
    if (slide || exiting) return;
    setOpen(false);
    const vw = window.innerWidth;
    const p = at || pos;
    const toRight = !p || (p.x + btn / 2) > vw / 2;
    setExiting({ toRight, key: Date.now(), x: p.x, y: p.y });
    setTimeout(() => setFabGone(true), 220);
    setTimeout(() => {
      setHidden(true);
      setExiting(null);
      setFabGone(false);
      try { if (hiddenKey) localStorage.setItem(hiddenKey, "1"); } catch (e) { /* ignore */ }
    }, EXIT_MS);
  };
  // return animation: empty badge, Buddy runs in from the edge he was hiding at and hops into it
  const [returning, setReturning] = useState(null); // null | { fromRight, key, x, y }
  const showBuddy = () => {
    if (returning) return;
    setFlashVisible(false);
    const vw = window.innerWidth;
    const fromRight = !pos || (pos.x + btn / 2) > vw / 2;
    setReturning({ fromRight, key: Date.now(), x: pos.x, y: pos.y });
    setNoBob(true);
    setTimeout(() => setHidden(false), RETURN_MS - 260); // FAB fades in under the last waving frames
    setTimeout(() => { setReturning(null); setNoBob(false); }, RETURN_MS);
    try { if (hiddenKey) localStorage.setItem(hiddenKey, "0"); } catch (e) { /* ignore */ }
  };
  // reminder / alarm while hidden: pop out briefly to notify, then hide again
  useEffect(() => {
    const flash = () => {
      setFlashVisible(true);
      clearTimeout(flashTimerRef.current);
      flashTimerRef.current = setTimeout(() => setFlashVisible(false), 12000);
    };
    const onNotify = (e) => { if (e?.detail?.ringing === false) return; flash(); };
    window.addEventListener("osc-alarm-ringing", onNotify);
    window.addEventListener("osc-buddy-flash", flash);
    return () => { window.removeEventListener("osc-alarm-ringing", onNotify); window.removeEventListener("osc-buddy-flash", flash); clearTimeout(flashTimerRef.current); };
  }, []);
  const effectivelyHidden = hidden && !flashVisible && !open && !interpOpen && mood === "idle" && !wakeAwake;
  const fabHidden = effectivelyHidden || (returning && hidden);

  // start/stop the listener
  useEffect(() => {
    if (!user || !wakeEnabled) return undefined;
    const listener = createWakeListener({
      onPrefix: (txt) => { wakePrefixRef.current = txt || ""; },
      lang: user.language || "en",
      onWake: () => {
        // echo guard: if Buddy's own spoken reply contains a wake-ish word,
        // the recognizer probably heard HIM — don't let him interrupt himself
        if (moodRef.current === "talking" && /budd?y|body|badi/i.test(speakingTextRef.current)) return;
        if (moodRef.current === "talking") stopSpeaking(); // barge-in: shut up and listen
        if (hidden) showBuddy(); // "Hey Buddy" while hidden → he slides back in
        setOpen(true);
        setWakeAwake(true);
        // hands-free flow: start VAD capture — Buddy notices when you stop talking
        startHandsFreeRef.current?.(false);
      },
      onCommand: (text) => {
        if (hidden) showBuddy();
        setOpen(true);
        if (moodRef.current === "talking") {
          if (/budd?y|body|badi/i.test(speakingTextRef.current)) return; // echo guard
          // barge-in: the inline command may be contaminated with Buddy's own
          // TTS words — cut the speech and capture a clean utterance instead
          stopSpeaking();
          setWakeAwake(true);
          startHandsFreeRef.current?.(false);
          return;
        }
        setWakeAwake(false);
        triggerGreetRef.current?.();
        if (moodRef.current === "idle") runAskRef.current?.({ text, handsFree: true });
      },
      onState: (s) => {
        if (s === "passive") setWakeAwake(false);
        if (s === "denied") {
          setWakeEnabled(false);
          try { localStorage.setItem(wakeKey, "0"); } catch (e) { /* ignore */ }
          toast.warning(t("buddy_mic_denied"));
        }
      },
    });
    wakeRef.current = listener;
    let cancelled = false;
    (async () => {
      // Native: open+close the mic ONCE before the recognizer starts. The very first
      // getUserMedia on iOS is slow (audio session + permission) and used to swallow the
      // first "Hey Buddy" / first mic tap — after this warm-up it is instant.
      if (Capacitor.isNativePlatform() && !micPrimedRef.current) {
        micPrimedRef.current = true;
        try {
          await warmMic();
          await new Promise((r) => setTimeout(r, 400));
        } catch (e) { /* permission denied → the recognizer will report it */ }
        releaseMic();
        await new Promise((r) => setTimeout(r, 800)); // let the session settle before the engine starts
      }
      if (!cancelled) listener.start();
    })();
    return () => { cancelled = true; listener.stop(); wakeRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, wakeEnabled]);

  // reminder alarm ringing? (see ReminderAlarm) — the wake recognizer MUST be
  // off while alarm audio plays/stops or iOS crashes on the session change
  const [alarmRinging, setAlarmRinging] = useState(false);
  useEffect(() => {
    const onRing = (e) => setAlarmRinging(!!e.detail?.ringing);
    window.addEventListener("osc-alarm-ringing", onRing);
    return () => window.removeEventListener("osc-alarm-ringing", onRing);
  }, []);

  // App sent to the background (phone): iOS tears the audio session down. A
  // hands-free capture, a hold-to-talk recording or Buddy's own voice left
  // running would come back to a dead engine and freeze the screen — stop them
  // all now and return to idle; the wake listener pauses itself (wakeword.js).
  useEffect(() => {
    const onHide = () => {
      if (!document.hidden) return;
      try { hfRef.current?.cancel?.(); } catch (e) { /* ignore */ }
      hfRef.current = null;
      try { recRef.current?.recorder?.stop?.(); } catch (e) { /* ignore */ }
      recRef.current = null;
      try { audioRef.current?.pause?.(); } catch (e) { /* ignore */ }
      audioRef.current = null;
      speakingTextRef.current = "";
      releaseMic();
      setHandsFree("off");
      setWakeAwake(false);
      if (moodRef.current !== "thinking") setMood("idle");
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Buddy must never hear himself think or record. Desktop web also keeps the
  // wake listener ON while he SPEAKS so "Hey Buddy" can interrupt (barge-in) —
  // but phones (iOS/Android app + mobile browsers) DUCK the app's audio the
  // moment the microphone turns on, which made Buddy's voice fade out ~1s into
  // every reply. On mobile the mic only comes back AFTER he finishes speaking.
  useEffect(() => {
    const l = wakeRef.current;
    if (!l) return;
    if (alarmRinging) { l.suspend(); return; }
    if (mood === "idle" || (mood === "talking" && bargeInCapable())) l.resume();
    else l.suspend();
  }, [mood, alarmRinging]);

  const toggleWake = () => {
    const next = !wakeEnabled;
    setWakeEnabled(next);
    setWakeAwake(false);
    if (!next && hfRef.current) { cancelHandsFree(); setMood("idle"); }
    try { localStorage.setItem(wakeKey, next ? "1" : "0"); } catch (e) { /* ignore */ }
    toast.info(next ? t("buddy_wake_on") : t("buddy_wake_off"));
  };

  // ---------- drag / tap ----------
  const onPointerDown = (e) => {
    if (!pos) return;
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (err) { /* pointer already gone */ }
    dragRef.current = { px: e.clientX, py: e.clientY, x: pos.x, y: pos.y, moved: false };
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.px;
    const dy = e.clientY - d.py;
    if (!d.moved && Math.abs(dx) + Math.abs(dy) > 7) {
      d.moved = true;
      setDragging(true);
      setOpen(false);
    }
    if (d.moved) setPos(clampPos(d.x + dx, d.y + dy, btn));
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    setDragging(false);
    if (d?.moved) {
      // dragged off the screen (pointer released past / hugging the edge) → he hides there
      const vw = window.innerWidth;
      const cx = e?.clientX ?? vw / 2;
      if (cx <= 24 || cx >= vw - 24) {
        const at = clampPos(d.x + (e.clientX - d.px), d.y + (e.clientY - d.py), btn);
        setPos(at);
        if (posKey) { try { localStorage.setItem(posKey, JSON.stringify(at)); } catch (err) { /* ignore */ } }
        hideBuddy(at); // same Buddy, same spot: the FAB crossfades into the running-off animation
        return;
      }
      setPos((p) => {
        if (p && posKey) {
          try { localStorage.setItem(posKey, JSON.stringify(p)); } catch (e) { /* ignore */ }
        }
        return p;
      });
    } else {
      setOpen((o) => {
        if (!o) triggerGreet(); // welcome wave when opening
        return !o;
      });
    }
  };

  // ---------- hold-to-talk ----------
  // A short tap, or releasing before the mic is ready (the first getUserMedia on a
  // phone can take ~1s), must NEVER be a dead click: it turns into a hands-free
  // listen — Buddy hears the sentence and stops by himself when the user pauses.
  const armingRef = useRef(false);     // startRecording is waiting for the mic stream
  const tapCancelledRef = useRef(false); // pointerdown cancelled an active listen — ignore its pointerup
  const releasedEarlyRef = useRef(false);
  const startRecording = async (e) => {
    e.preventDefault();
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch (err) { /* pointer already gone */ }
    if (recRef.current || armingRef.current || mood === "thinking") return;
    if (hfRef.current) {
      // Buddy is already listening (tap / "Hey Buddy"): this tap CANCELS — stop listening,
      // discard what was captured, back to passive until the next tap or "Hey Buddy"
      cancelHandsFree();
      tapCancelledRef.current = true;
      setWakeAwake(false);
      setMood("idle"); // passive again — wake listener resumes on "idle"
      chime("end");
      return;
    }
    // stop the native wake recognizer BEFORE grabbing the mic — reconfiguring
    // the iOS audio session under a running SFSpeechRecognizer can crash the
    // whole app (AVAudioEngine format exception)
    try { wakeRef.current?.suspend(); } catch (err) { /* ignore */ }
    armingRef.current = true;
    releasedEarlyRef.current = false;
    setMood("listening"); // instant feedback while the mic spins up
    try {
      const stream = await warmMic();
      if (releasedEarlyRef.current) {
        // finger already lifted → tap-to-talk: listen hands-free on the (now warm) mic
        armingRef.current = false;
        startHandsFree(true); // sets mood "listening" itself — never bounce through idle (wake would restart)
        return;
      }
      const mime = pickUtteranceMime(); // Apple-first: iOS webm recorders yield empty blobs
      const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorder.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
      recRef.current = { recorder, chunks, startedAt: Date.now() };
      recorder.start(150);
      try { audioRef.current?.pause?.(); } catch (err) { /* ignore */ }
    } catch (err) {
      releaseMic();
      setMood("idle");
      toast.warning(t("buddy_mic_denied"));
    } finally {
      armingRef.current = false;
    }
  };

  const stopRecording = async () => {
    if (tapCancelledRef.current) { tapCancelledRef.current = false; return; } // the press was a "cancel listening" tap
    const rec = recRef.current;
    if (!rec) {
      if (armingRef.current) releasedEarlyRef.current = true; // handled when the mic arrives
      return;
    }
    recRef.current = null;
    const heldMs = Date.now() - rec.startedAt;
    const stopped = new Promise((resolve) => { rec.recorder.onstop = resolve; });
    try { rec.recorder.stop(); } catch (err) { /* ignore */ }
    await stopped;
    if (heldMs < 350) {
      // quick tap → hands-free listen (mic stays warm; wake stays suspended until idle)
      startHandsFree(true);
      return;
    }
    const type = rec.recorder.mimeType || "audio/webm";
    const blob = new Blob(rec.chunks, { type });
    if (!blob.size) {
      releaseMic();
      setMood("idle");
      return;
    }
    await runAsk({ blob, filename: type.includes("mp4") ? "buddy.m4a" : "buddy.webm" });
  };

  const runAsk = async ({ blob = null, filename = "", text = "", handsFree: hf = false, withLocation = false, prefix = "" }) => {
    setMood("thinking");
    // iOS keeps the audio session in "record" mode while ANY mic stream is
    // open, which ducks (fades out) Buddy's spoken reply. The utterance is
    // already captured at this point — release the mic NOW so the session
    // returns to playback mode before the TTS starts.
    releaseMic();
    try {
      const fd = new FormData();
      fd.append("user_id", user.id);
      fd.append("client_time", localISO());
      fd.append("history_json", JSON.stringify(
        log.slice(-8).map((l) => ({ role: l.role === "buddy" ? "assistant" : "user", content: l.text }))
      ));
      if (text) fd.append("text", text);
      if (blob) fd.append("audio", blob, filename);
      fd.append("speak", voiceOnRef.current ? "1" : "0"); // muted speaker = text only (skip TTS)
      fd.append("tts_split", "1"); // this build supports two-phase TTS (/buddy/tts-rest)
      if (prefix) fd.append("prefix_text", prefix); // start of the sentence heard by the wake-word recognizer
      if (draftRef.current) {
        // a draft card is waiting for Enviar/Cancelar: the backend decides if these words approve/reject it
        const pd = draftRef.current;
        fd.append("pending_draft", JSON.stringify({ type: pd.type, recipient_name: pd.recipient_name, group_name: pd.group_name,
          text: (pd.text || pd.message || "").slice(0, 300), title: pd.title, fact: pd.fact }));
      }
      // device location (distances / ETA / route): cached fix, or a fresh one after the permission prompt
      const pos = withLocation ? await getPosition({ prompt: true }) : cachedPosition();
      if (pos) { fd.append("lat", String(pos.lat)); fd.append("lng", String(pos.lng)); }
      warmPosition();
      const r = await api.post("/buddy/ask", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { transcript, reply, audio_base64, actions, farewell, tts_rest, tts_rest_text } = r.data || {};

      // Voice confirmation of a pending draft ("pode enviar", "sim", "cancela"...): the backend decides
      // (fast word match, or the model via draft_decision); the local regexes are the fallback.
      const said = (transcript || text || "").trim();
      const dd = (actions || []).find((a) => a.type === "draft_decision");
      if (draftRef.current && (dd || said)) {
        const decision = dd?.value || (CONFIRM_RE.test(said) ? "confirm" : CANCEL_RE.test(said) ? "cancel" : null);
        if (decision === "confirm") {
          if (said) setLog((prev) => [...prev, { role: "user", text: said, ts: new Date().toISOString() }]);
          await confirmDraft();
          setMood("idle");
          return;
        }
        if (decision === "cancel") {
          setLog((prev) => [...prev, ...(said ? [{ role: "user", text: said, ts: new Date().toISOString() }] : []), { role: "buddy", text: t("buddy_draft_discarded"), ts: new Date().toISOString() }]);
          setDraft(null);
          setMood("idle");
          return;
        }
      }

      const ja = (actions || []).find((a) => a.type === "job_answer");
      const nowTs = new Date().toISOString();
      setLog((prev) => [
        ...prev,
        ...(transcript ? [{ role: "user", text: transcript, ts: nowTs }] : []),
        { role: "buddy", text: reply || "", ts: nowTs, job: ja ? { id: ja.job_id, label: ja.job_label } : undefined },
      ]);
      const d = (actions || []).find((a) => (a.type || "").startsWith("draft_"));
      setDraft(d || null);
      // voice measurement session: live card + camera for a unit
      const ms = (actions || []).filter((a) => a.type === "measure_session").pop();
      if (ms?.session) setMeasure(ms.session.status === "cancelled" ? null : ms.session);
      const ts = (actions || []).filter((a) => a.type === "trim_session").pop();
      if (ts?.session) setTrim(ts.session.status === "cancelled" ? null : ts.session);
      const cam = (actions || []).find((a) => a.type === "open_camera");
      if (cam?.session_id) setTimeout(() => takeUnitPhoto(cam.session_id, cam.unit_n), 400);
      const sv = (actions || []).find((a) => a.type === "save_photo_to_phone");
      if (sv?.media_id) savePhotoToPhone(sv.media_id);
      // Buddy created/wrote into a chat (My Notes / Buddy chat) -> take the user there
      const oc = (actions || []).find((a) => a.type === "open_conversation");
      if (oc?.conversation_id) {
        window.dispatchEvent(new CustomEvent("osc-conversations-changed"));
        setTimeout(() => navigate(`/chat/${oc.conversation_id}${oc.attach ? `?attach=${oc.attach}` : ""}`), 600);
        if (oc.attach) setOpen(false);
      }
      // Buddy needs where the user is: ask the OS permission now and repeat the question with the fix
      if (!withLocation && (actions || []).some((a) => a.type === "need_location")) {
        const got = await getPosition({ prompt: true });
        if (got) {
          setLog((prev) => prev.slice(0, -1)); // drop the "need permission" line — the real answer follows
          return runAsk({ text: transcript || text, handsFree: hf, withLocation: true });
        }
      }
      const ou = (actions || []).find((a) => a.type === "open_url");
      if (ou?.url) { setOpen(false); setTimeout(() => openExternal(ou.url), 900); }
      const inv = (actions || []).find((a) => a.type === "open_invite");
      if (inv?.identifier) { setOpen(false); setTimeout(() => setInviteTarget(inv.identifier), 400); }
      const ch = (actions || []).find((a) => a.type === "open_chatter");
      if (ch?.job_id) setTimeout(() => setChatterTarget({ jobId: ch.job_id, date: ch.date, jobLabel: ch.job_label }), 400);
      // Phase 4 — app control: navigate / theme / font / notifications / offline / hide
      const ac = (actions || []).filter((a) => a.type === "app_control");
      let hideAfter = false;
      for (const a of ac) {
        if (a.op === "navigate" && a.path) { setOpen(false); setTimeout(() => navigate(a.path), 600); }
        else if (a.op === "go_back") { setOpen(false); setTimeout(() => { if ((window.history.state?.idx ?? 0) > 0) navigate(-1); else navigate("/home"); }, 600); }
        else if (a.op === "theme") { setTheme(a.value); window.dispatchEvent(new CustomEvent("osc-settings-changed")); }
        else if (a.op === "font_size") { setFontSize(a.value); window.dispatchEvent(new CustomEvent("osc-settings-changed")); }
        else if (a.op === "notifications") {
          localStorage.setItem("pmf_notifications", a.value === "on" ? "on" : "off");
          if (a.value === "on" && "Notification" in window && Notification.permission === "default") Notification.requestPermission().catch(() => {});
          window.dispatchEvent(new CustomEvent("osc-settings-changed"));
        }
        else if (a.op === "offline_mode") {
          if (a.value === "on") { setOpen(false); setTimeout(() => navigate("/settings?s=offline&setup=1"), 600); }
          else if (user?.id) { saveOfflineSettings(user.id, { enabled: false }); clearOfflineData(user.id).catch(() => {}); }
        }
        else if (a.op === "hide_buddy") hideAfter = true;
      }
      // Voice command "estou com o cliente" -> open the live interpreter.
      // Skip the reply TTS: the interpreter speaks the introduction right away.
      if ((actions || []).some((a) => a.type === "interpreter")) {
        openInterpreter();
        return;
      }
      // hands-free dialogue: when the spoken reply ends, listen again for a
      // follow-up — unless the user said goodbye or closed the balloon.
      // The backend flags farewells too (LLM-side goodbye like "Até mais!").
      // Courtesy closers also end the chat: user thanking at the END of a
      // sentence, or Buddy replying "De nada!..." (unless a draft is pending).
      const isBye = !!farewell || isFarewell(said) || (!d && isAckCloser(said)) ||
        (!d && (TRAILING_THANKS_RE.test(normText(said)) || REPLY_CLOSER_RE.test(normText(reply))));
      const afterReply = () => {
        // always leave "thinking/talking" — never get stuck if listening can't start
        speakingTextRef.current = "";
        setMood("idle");
        moodRef.current = "idle";
        if (hideAfter) { setWakeAwake(false); setHandsFree("off"); hideBuddy(); return; }
        if (hf && !isBye && openRef.current && wakeRef.current) {
          startHandsFreeRef.current?.(true);
        } else if (isBye) {
          // goodbye: fully back to passive mode — waiting for "Hey Buddy" / hold-to-talk
          setWakeAwake(false);
          setHandsFree("off");
        }
      };
      if (audio_base64 && voiceOnRef.current) {
        // Two-phase TTS: part 1 arrives inline so Buddy starts talking fast;
        // the rest is fetched WHILE part 1 plays and continues seamlessly.
        let restPromise = null;
        const fetchRest = () => {
          // user_id + text let the server re-synthesize when the token landed on another worker
          const qs = `?user_id=${encodeURIComponent(user.id)}&text=${encodeURIComponent(tts_rest_text || "")}`;
          return api.get(`/buddy/tts-rest/${tts_rest}${qs}`, { timeout: 25000 })
            .then((res) => res.data?.audio_base64 || null)
            .catch(() => null);
        };
        if (tts_rest) restPromise = fetchRest();
        const seg = { current: null, cancelled: false };
        const playRest = async () => {
          if (seg.cancelled) return;
          let restB64 = restPromise ? await restPromise : null;
          if (!restB64 && tts_rest && !seg.cancelled) restB64 = await fetchRest(); // one retry — never stop mid-sentence silently
          if (seg.cancelled) return;
          if (restB64) {
            try {
              const h2 = await playVoice(restB64, { onEnd: afterReply });
              if (h2) { seg.current = h2; return; }
            } catch (e) { /* fall through */ }
          }
          afterReply();
        };
        // Web Audio playback: immune to desktop autoplay blocking once unlocked
        const handle = await playVoice(audio_base64, {
          onStart: () => {
            speakingTextRef.current = reply || ""; // barge-in echo guard
            setMood("talking");
            if (isBye) triggerGreetRef.current?.(); // goodbye wave while saying "Até mais!"
          },
          onEnd: tts_rest ? playRest : afterReply,
        });
        if (handle) {
          seg.current = handle;
          // composite handle: pausing also cancels the pending second phase
          audioRef.current = {
            pause: () => {
              seg.cancelled = true;
              try { seg.current?.pause?.(); } catch (e) { /* ignore */ }
            },
          };
        } else {
          if (isBye) triggerGreetRef.current?.();
          afterReply(); // playback unavailable — text-only, keep the flow going
        }
      } else {
        if (isBye) triggerGreetRef.current?.(); // goodbye wave (text-only mode)
        afterReply();
      }
    } catch (err) {
      setMood("idle");
      setLog((prev) => [...prev, { role: "buddy", text: t("buddy_error"), ts: new Date().toISOString() }]);
    }
  };
  runAskRef.current = runAsk;

  // ---------- voice measurement session: photo of a unit / finish / cancel ----------
  const attachUnitPhoto = async (sessionId, n, dataUrl) => {
    setMeasureBusy(true);
    try {
      const media = await uploadMedia({
        base64: dataUrlToBase64(dataUrl), contentType: "image/jpeg", kind: "image",
        jobId: measure?.job_id || null, uploadedBy: user.id, filename: `measure-voice-${Date.now()}.jpg`,
      });
      const r = await api.post(`/buddy/measure-session/${sessionId}/photo`, { user_id: user.id, unit_n: n, media_id: media.id });
      if (r.data?.session) setMeasure(r.data.session);
      soundSwoosh();
      setTimeout(() => runAskRef.current?.({ text: `[photo_attached:${n}]`, handsFree: true }), 300);
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally {
      setMeasureBusy(false);
    }
  };
  const savePhotoToPhone = async (mediaId) => {
    if (!galleryAvailable()) { toast.info(t("buddy_measure_save_native_only")); return; }
    try {
      const n = await saveToGallery([{ path: mediaSrcById(mediaId), kind: "photo" }], null);
      if (n) toast.success(t("saved_to_gallery_n").replace("{n}", String(n)));
    } catch (e) { toast.error(t("something_wrong")); }
  };
  const takeUnitPhoto = async (sessionId, n) => {
    try {
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("Camera")) {
        const { Camera, CameraResultType, CameraSource } = await import("@capacitor/camera");
        const shot = await Camera.getPhoto({ quality: 85, width: 1600, resultType: CameraResultType.DataUrl, source: CameraSource.Camera, saveToGallery: true, correctOrientation: true });
        if (shot?.dataUrl) await attachUnitPhoto(sessionId, n, shot.dataUrl);
        return;
      }
    } catch (e) { return; /* user cancelled */ }
    measurePhotoTargetRef.current = { sessionId, n };
    measurePhotoRef.current?.click();
  };
  const onMeasurePhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    const tgt = measurePhotoTargetRef.current;
    if (!file || !tgt) return;
    try {
      const { dataUrl } = await compressImage(file);
      await attachUnitPhoto(tgt.sessionId, tgt.n, dataUrl);
    } catch (err) { toast.error(t("something_wrong")); }
  };
  const trimOp = async (op) => {
    if (!trim) return;
    setTrimBusy(true);
    try {
      const r = await api.post(`/buddy/trim-session/${trim.id}/${op}`, null, { params: { user_id: user.id } });
      const sess = r.data?.session;
      if (op === "cancel") { setTrim(null); setLog((prev) => [...prev, { role: "buddy", text: t("buddy_trim_cancelled"), ts: new Date().toISOString() }]); }
      else { setTrim(sess); soundHammer(); setLog((prev) => [...prev, { role: "buddy", text: t("buddy_trim_saved_msg"), ts: new Date().toISOString() }]); window.dispatchEvent(new CustomEvent("osc-conversations-changed")); }
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally {
      setTrimBusy(false);
    }
  };
  const measureOp = async (op) => {
    if (!measure) return;
    setMeasureBusy(true);
    try {
      const r = await api.post(`/buddy/measure-session/${measure.id}/${op}`, null, { params: { user_id: user.id } });
      const sess = r.data?.session;
      if (op === "cancel") { setMeasure(null); setLog((prev) => [...prev, { role: "buddy", text: t("buddy_measure_cancelled"), ts: new Date().toISOString() }]); }
      else { setMeasure(sess); soundHammer(); setLog((prev) => [...prev, { role: "buddy", text: t("buddy_measure_saved_msg"), ts: new Date().toISOString() }]); window.dispatchEvent(new CustomEvent("osc-conversations-changed")); }
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally {
      setMeasureBusy(false);
    }
  };

  // ---------- confirm draft (message / group / task / issue) ----------
  const confirmDraft = async () => {
    const d = draftRef.current;
    if (!d || sendingRef.current) return;
    sendingRef.current = true;
    setSendingDraft(true);
    try {
      let doneText = t("buddy_sent");
      if (d.type === "draft_message") {
        await api.post("/buddy/confirm-send", {
          user_id: user.id, recipient_id: d.recipient_id, text: d.text,
        });
        doneText = `${t("buddy_sent")} (${d.recipient_name})`;
      } else if (d.type === "draft_memory") {
        await api.post("/buddy/memories", { user_id: user.id, text: d.fact });
        doneText = t("buddy_memory_saved");
      } else {
        const res = await api.post("/buddy/confirm-action", { user_id: user.id, action: d });
        if (d.type === "draft_task") doneText = t("buddy_task_created");
        else if (d.type === "draft_forward") doneText = t("buddy_media_sent");
        else if (d.type === "draft_edit_message") doneText = t("buddy_msg_edited");
        else if (d.type === "draft_delete_message") doneText = t("buddy_msg_deleted");
        else if (d.type === "draft_issue_status") doneText = t("buddy_issue_updated");
        else if (d.type === "draft_task_done") doneText = d.done === false ? t("buddy_task_reopened") : t("buddy_task_done");
        else if (d.type === "draft_group_create") {
          doneText = t("buddy_group_created");
          window.dispatchEvent(new CustomEvent("osc-conversations-changed"));
          if (res.data?.conversation_id) navigate(`/chat/${res.data.conversation_id}`);
        }
        else if (d.type === "draft_issue") doneText = t("buddy_issue_created");
        else if (d.type === "draft_knowledge") doneText = d.op === "delete" ? t("buddy_know_deleted") : t("buddy_know_updated");
        else if (d.type === "draft_intro") doneText = t("interp_intro_saved");
        else if (d.type === "draft_delete_conversation") { doneText = t("buddy_conv_deleted"); window.dispatchEvent(new CustomEvent("osc-conversations-changed")); }
        else if (d.type === "draft_account_decision") doneText = d.action === "reject" ? t("buddy_admin_rejected") : t("buddy_admin_approved");
        else if (d.type === "draft_broadcast") doneText = `${t("buddy_admin_broadcast_sent")} (${res.data?.recipients ?? d.audience_count ?? 0})`;
        else if (d.type === "draft_user_update") doneText = t("buddy_admin_user_updated");
        else if (d.type === "draft_user_delete") doneText = t("buddy_admin_user_deleted");
        else if (d.type === "draft_company_decision") doneText = d.kind === "remove_member" ? t("buddy_company_removed") : d.action === "reject" ? t("buddy_admin_rejected") : t("buddy_admin_approved");
        else if (d.type === "draft_measure_send" || d.type === "draft_trim_send") doneText = `${t("buddy_sent")} (${d.label})`;
        else doneText = `${t("buddy_sent")} (${d.group_name})`;
      }
      // friendly tool sounds
      if (d.type === "draft_task" || d.type === "draft_memory" || d.type === "draft_knowledge" || d.type === "draft_intro") soundHammer();
      else if (d.type === "draft_issue") soundDrill();
      else soundSwoosh();
      toast.success(doneText);
      setLog((prev) => [...prev, { role: "buddy", text: doneText, ts: new Date().toISOString() }]);
      setDraft(null);
    } catch (err) {
      toast.error(t("buddy_error"));
    } finally {
      sendingRef.current = false;
      setSendingDraft(false);
    }
  };

  if (!user || !pos) return null;

  // ---------- balloon geometry ----------
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // iPhone notch / home-indicator: keep the balloon inside the safe area
  let safeTop = 0, safeBottom = 0;
  try {
    const cs = getComputedStyle(document.documentElement);
    safeTop = parseFloat(cs.getPropertyValue("--safe-top")) || 0;
    safeBottom = parseFloat(cs.getPropertyValue("--safe-bottom")) || 0;
  } catch (e) { /* ignore */ }
  const bw = Math.min(340, vw - 20);
  const bLeft = Math.min(Math.max(pos.x + btn / 2 - bw / 2, 10), vw - bw - 10);
  const below = pos.y < vh * 0.42;
  // never let the balloon escape the viewport: cap its height to the space available
  const bMaxH = Math.max(240, below ? vh - (pos.y + btn + 14) - safeBottom - 10 : pos.y - 14 - safeTop - 10);
  const tailX = Math.min(Math.max(pos.x + btn / 2 - bLeft - 9, 18), bw - 30);
  const faceMood = wakeAwake && mood === "idle" ? "listening" : mood;
  const statusText = handsFree === "waiting" ? t("buddy_speak_now")
    : handsFree === "recording" ? t("buddy_listening")
    : wakeAwake && mood === "idle" ? t("buddy_listening")
    : mood === "listening" ? t("buddy_listening")
    : mood === "thinking" ? t("buddy_thinking")
    : mood === "talking" ? t("buddy_speaking") : "";

  return (
    <>
      <ReminderAlarm />
      <InviteDialog identifier={inviteTarget} kind="connect" onClose={() => setInviteTarget(null)} />
      <ChatterExpressDialog open={!!chatterTarget} onOpenChange={(o) => { if (!o) setChatterTarget(null); }}
        jobId={chatterTarget?.jobId} date={chatterTarget?.date} jobLabel={chatterTarget?.jobLabel} />
      {interpOpen && <InterpreterMode onClose={closeInterpreter} />}
      {/* Hidden: peek from the edge where he was parked (tap = bring back) */}
      {effectivelyHidden && !returning && (
        <BuddyPeek pos={pos} size={btn} onTap={() => { showBuddy(); }}
          onDragY={(y, done) => {
            setPos((p) => {
              if (!p) return p;
              const np = clampPos(p.x, y, btn);
              if (done && posKey) { try { localStorage.setItem(posKey, JSON.stringify(np)); } catch (e) { /* ignore */ } }
              return np;
            });
          }} />
      )}
      {/* Floating Buddy face */}
      {/* Exit animation: hops out of the badge and runs off (mirrored when hiding to the left) */}
      {exiting && (() => {
        const face = Math.round(btn * 1.32);
        const box = Math.round(face / EXIT_BADGE_FRAC * 0.64); // exit animation 36% smaller than the FAB face (two -20% steps, user request)
        const cx = exiting.x + btn / 2, cy = exiting.y + btn / 2;
        const bx = exiting.toRight ? EXIT_BADGE_CX : 1 - EXIT_BADGE_CX;
        const left = cx - bx * box;
        // while he runs, the frame glides so its exit edge reaches the screen edge → he leaves the SCREEN, not the box
        const dx = exiting.toRight ? (window.innerWidth + 6) - (left + box) : -(left + 6);
        return (
          <div
            key={exiting.key}
            data-testid="buddy-exit-anim"
            className="fixed z-[46] pointer-events-none select-none buddy-exit-fade buddy-exit-glide"
            style={{ left, top: cy - EXIT_BADGE_CY * box, width: box, height: box, "--exit-dx": `${Math.round(dx)}px` }}
          >
            <img
              src={`${buddyExit}?r=${exiting.key}`} /* fresh URL → WebKit replays the one-shot animation */
              alt=""
              draggable={false}
              className="block w-full h-full"
              style={{ transform: exiting.toRight ? undefined : "scaleX(-1)" }}
            />
          </div>
        );
      })()}
      {/* Return animation: runs in from the edge (mirrored when he was hiding on the right) */}
      {returning && (() => {
        const face = Math.round(btn * 1.32);
        const box = Math.round(face / RET_BADGE_FRAC * 0.51); // return animation 20% smaller than the exit one (user request)
        const cx = returning.x + btn / 2, cy = returning.y + btn / 2;
        const left = cx - RET_BADGE_CX * box;
        const dx = returning.fromRight ? (window.innerWidth + 6) - (left + box) : -(left + 6);
        return (
          <div
            key={returning.key}
            data-testid="buddy-return-anim"
            className="fixed z-[46] pointer-events-none select-none buddy-return-glide"
            style={{ left, top: cy - RET_BADGE_CY * box, width: box, height: box, "--exit-dx": `${Math.round(dx)}px` }}
          >
            <img src={`${buddyReturn}?r=${returning.key}`} alt="" draggable={false} className="block w-full h-full" style={{ transform: returning.fromRight ? "scaleX(-1)" : undefined }} />
          </div>
        );
      })()}
      {!fabHidden && !(exiting && fabGone) && <button
        data-testid="buddy-btn"
        aria-label="Buddy"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
        style={{ left: exiting ? exiting.x : pos.x, top: exiting ? exiting.y : pos.y, width: btn, height: btn, touchAction: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none",
          opacity: exiting ? 0 : 1, pointerEvents: exiting ? "none" : undefined,
          animation: returning ? "buddy-return-fab .26s ease-out both" : undefined,
          transform: slide ? `translateX(${edgeShift()}px)` : undefined,
          transition: exiting ? "opacity .22s ease-out" : slide === "out" ? "transform .65s cubic-bezier(.4,0,.2,1)" : slide === "" && !dragging ? "transform .65s cubic-bezier(.2,.8,.2,1)" : "none" }}
        className={`fixed z-[45] rounded-full
          flex items-center justify-center select-none cursor-grab active:cursor-grabbing
          ${dragging ? "scale-110" : (slide || noBob || exiting || returning) ? "" : "buddy-bob"}`}
      >
        <BuddyFace mood={faceMood} greet={greet} size={Math.round(btn * 1.32)} />
        {(mood === "listening" || wakeAwake) && <span className="absolute inset-0 rounded-full buddy-ring border-2 border-destructive" />}
        {wakeEnabled && (
          <span
            data-testid="buddy-wake-dot"
            className="absolute bottom-0.5 right-0.5 h-3 w-3 rounded-full bg-emerald-500 border-2 border-card"
            title='"Hey Buddy"'
          />
        )}
        {!dragging && !open && (
          <span
            role="button"
            data-testid="buddy-hide-btn"
            aria-label="hide Buddy"
            onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
            onPointerUp={(e) => { e.stopPropagation(); e.preventDefault(); hideBuddy(); }}
            className="absolute -top-1 -left-1 h-5 w-5 rounded-full bg-card border shadow flex items-center justify-center text-muted-foreground active:scale-95"
          >
            <Minus className="h-3 w-3" />
          </span>
        )}
      </button>}

      {/* Speech balloon panel */}
      {open && !dragging && (
        <div
          data-testid="buddy-balloon"
          style={{
            left: bLeft,
            width: bw,
            maxHeight: bMaxH,
            ...(below ? { top: pos.y + btn + 14 } : { bottom: vh - pos.y + 14 }),
          }}
          className="fixed z-[46] rounded-2xl border-2 border-border bg-card shadow-2xl flex flex-col"
        >
          <div
            style={{ left: tailX, [below ? "top" : "bottom"]: -9 }}
            className={`absolute h-4 w-4 rotate-45 bg-card ${below ? "border-l-2 border-t-2" : "border-r-2 border-b-2"} border-border`}
          />
          {/* header — row 1: identity, row 2: toolbar (fits narrow mobile balloons) */}
          <div className="border-b shrink-0">
            <div className="flex items-center gap-2 px-3 pt-2">
              <div className="h-9 w-9 flex items-center justify-center shrink-0">
                <BuddyFace mood={faceMood} greet={greet} size={40} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold leading-tight" data-testid="buddy-title">Buddy</div>
                <div className="text-[11px] text-muted-foreground leading-tight truncate" data-testid="buddy-status">
                  {statusText || t("buddy_tagline")}
                </div>
              </div>
              <button
                data-testid="buddy-close"
                onClick={() => {
                  if (hfRef.current) { cancelHandsFree(); setMood("idle"); }
                  setView("chat");
                  setOpen(false);
                }}
                className="h-8 w-8 shrink-0 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-1 px-2 pb-2 pt-1.5">
              <button
                data-testid="buddy-interpreter-btn"
                onClick={openInterpreter}
                title={t("interp_open_btn")}
                className="h-7 shrink-0 flex items-center gap-1.5 rounded-full border bg-primary/10 text-primary border-primary/40 hover:bg-primary/20 px-2.5 text-[11px] font-bold"
              >
                <Languages className="h-3.5 w-3.5" />
                <span className="truncate max-w-[110px]">{t("interp_title")}</span>
              </button>
              <div className="flex-1" />
              {wakeWordSupported() && (
                <button
                  data-testid="buddy-wake-toggle"
                  onClick={toggleWake}
                  title={t("buddy_wake_label")}
                  className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-full border
                    ${wakeEnabled ? "bg-emerald-500/15 text-emerald-600 border-emerald-500/50" : "text-muted-foreground border-transparent hover:bg-muted"}`}
                >
                  {wakeEnabled ? <Ear className="h-4 w-4" /> : <EarOff className="h-4 w-4" />}
                </button>
              )}
              <button
                data-testid="buddy-voice-toggle"
                onClick={toggleVoice}
                title={voiceOn ? t("buddy_voice_on") : t("buddy_voice_off")}
                className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-full border
                  ${voiceOn ? "bg-sky-500/15 text-sky-600 border-sky-500/50" : "text-muted-foreground border-transparent hover:bg-muted"}`}
              >
                {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              </button>
              {voiceOn && (
                <button
                  data-testid="buddy-speed-toggle"
                  onClick={cycleSpeed}
                  title={t("buddy_speed_label")}
                  className={`h-7 min-w-7 px-1 shrink-0 flex items-center justify-center rounded-full border text-[10px] font-extrabold tabular-nums
                    ${speed !== 1 ? "bg-sky-500/15 text-sky-600 border-sky-500/50" : "text-muted-foreground border-transparent hover:bg-muted"}`}
                >
                  {speed === 1 ? "1x" : speed === 1.5 ? "1.5x" : "2x"}
                </button>
              )}
              <button
                data-testid="buddy-sfx-toggle"
                onClick={cycleSfx}
                title={t(`buddy_sfx_${sfxKey}`)}
                className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-full border relative
                  ${sfxKey === "on" ? "bg-amber-500/15 text-amber-600 border-amber-500/50"
                    : sfxKey === "low" ? "bg-amber-500/10 text-amber-600/60 border-amber-500/30"
                    : "text-muted-foreground border-transparent hover:bg-muted"}`}
              >
                <Hammer className="h-4 w-4" />
                {sfxKey === "off" && <span className="absolute w-5 h-[1.5px] bg-current rotate-45 rounded" />}
                {sfxKey === "low" && <span className="absolute -bottom-0.5 text-[8px] font-bold leading-none">%</span>}
              </button>
              <button
                data-testid="buddy-memories-btn"
                onClick={toggleMemoriesView}
                title={t("buddy_memories_label")}
                className={`h-7 w-7 shrink-0 flex items-center justify-center rounded-full border
                  ${view === "memories" ? "bg-violet-500/15 text-violet-600 border-violet-500/50" : "text-muted-foreground border-transparent hover:bg-muted"}`}
              >
                <Brain className="h-4 w-4" />
              </button>
              <button
                data-testid="buddy-size-toggle"
                onClick={cycleSize}
                title={t("buddy_size_label")}
                className="h-7 w-7 shrink-0 flex items-center justify-center rounded-full border border-transparent hover:bg-muted text-[11px] font-extrabold text-muted-foreground"
              >
                {sizeKey.toUpperCase()}
              </button>
            </div>
          </div>

          {view === "memories" ? (
            /* ---------- memories view: review / add / edit / delete ---------- */
            <div className="max-h-[52vh] min-h-0 overflow-y-auto px-3 py-2 space-y-2" data-testid="buddy-memories-view">
              <div className="text-[11px] text-muted-foreground" data-testid="buddy-memories-hint">
                {t("buddy_memories_hint")}
              </div>
              {/* personality: how THIS user's Buddy behaves */}
              <div className="rounded-xl border bg-muted/30 px-3 py-2 space-y-1.5" data-testid="buddy-personality">
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  {t("buddy_personality")}
                </div>
                <div className="flex gap-1 flex-wrap">
                  {["default", "formal", "casual", "direct"].map((s) => (
                    <button
                      key={s}
                      data-testid={`buddy-style-${s}`}
                      onClick={() => { setMemStyle(s); savePrefs(s); }}
                      className={`text-[11px] font-semibold rounded-full px-2.5 py-1 border
                        ${memStyle === s ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground border-border hover:bg-muted"}`}
                    >
                      {t(`buddy_style_${s}`)}
                    </button>
                  ))}
                </div>
                <Textarea
                  data-testid="buddy-instructions-input"
                  value={memInstr}
                  onChange={(e) => setMemInstr(e.target.value)}
                  placeholder={t("buddy_instructions_ph")}
                  rows={2}
                  className="text-[12px] resize-none"
                />
                <div className="flex justify-end">
                  <Button
                    data-testid="buddy-prefs-save-btn"
                    size="sm" className="h-7 text-xs"
                    disabled={prefsBusy}
                    onClick={() => savePrefs()}
                  >
                    {prefsBusy ? "..." : t("buddy_prefs_save")}
                  </Button>
                </div>
              </div>
              <div className="flex gap-1.5 items-stretch">
                <Textarea
                  data-testid="buddy-memory-add-input"
                  value={memNew}
                  onChange={(e) => setMemNew(e.target.value)}
                  placeholder={t("buddy_memory_add_ph")}
                  rows={2}
                  className="text-[13px] min-h-[48px] resize-none flex-1"
                />
                <Button
                  data-testid="buddy-memory-add-btn"
                  size="sm"
                  className="h-auto px-3"
                  disabled={!memNew.trim() || memBusy}
                  onClick={addMemory}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              {memLoading && (
                <div className="text-xs text-muted-foreground py-2 text-center">...</div>
              )}
              {!memLoading && memories.length === 0 && (
                <div className="text-xs text-muted-foreground bg-muted/60 rounded-xl px-3 py-2" data-testid="buddy-memories-empty">
                  {t("buddy_memories_empty")}
                </div>
              )}
              {memories.map((m) => (
                <div key={m.id} data-testid="buddy-memory-item" className="rounded-xl border bg-muted/40 px-3 py-2">
                  {memEditId === m.id ? (
                    <div className="space-y-1.5">
                      <Textarea
                        data-testid="buddy-memory-edit-input"
                        value={memEditText}
                        onChange={(e) => setMemEditText(e.target.value)}
                        rows={3}
                        className="text-[13px] resize-none"
                      />
                      <div className="flex gap-1.5 justify-end">
                        <Button
                          data-testid="buddy-memory-edit-cancel"
                          size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => { setMemEditId(null); setMemEditText(""); }}
                        >
                          {t("buddy_cancel")}
                        </Button>
                        <Button
                          data-testid="buddy-memory-save-btn"
                          size="sm" className="h-7 text-xs"
                          disabled={!memEditText.trim() || memBusy}
                          onClick={saveMemoryEdit}
                        >
                          <Check className="h-3.5 w-3.5 mr-1" /> OK
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-1.5">
                      <div className="flex-1 text-[13px] leading-snug" data-testid="buddy-memory-text">{m.text}</div>
                      <button
                        data-testid="buddy-memory-edit-btn"
                        title={t("edit") || "Edit"}
                        onClick={() => { setMemEditId(m.id); setMemEditText(m.text); }}
                        className="h-6 w-6 flex items-center justify-center rounded-full hover:bg-muted text-muted-foreground shrink-0"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid="buddy-memory-delete-btn"
                        title={t("delete") || "Delete"}
                        onClick={() => deleteMemory(m.id)}
                        disabled={memBusy}
                        className="h-6 w-6 flex items-center justify-center rounded-full hover:bg-destructive/10 text-destructive shrink-0 disabled:opacity-50"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                  {memEditId !== m.id && m.created_at && (
                    <div className="text-[10px] text-muted-foreground mt-1">
                      {new Date(m.created_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
          <>
          {/* conversation log */}
          <div className="max-h-[38vh] min-h-0 overflow-y-auto px-3 py-2 space-y-2" data-testid="buddy-log">
            {histHasMore && (
              <button
                data-testid="buddy-load-older"
                onClick={() => {
                  const oldest = log.find((l) => l.ts)?.ts || "";
                  loadHistory(oldest);
                }}
                disabled={histLoading}
                className="w-full text-[11px] font-semibold text-primary py-1 rounded-lg hover:bg-muted disabled:opacity-50"
              >
                {histLoading ? "..." : `\u2191 ${t("buddy_load_older")}`}
              </button>
            )}
            {log.length === 0 && (
              <div className="text-xs text-muted-foreground bg-muted/60 rounded-xl px-3 py-2">
                {t("buddy_intro")}
              </div>
            )}
            {log.map((l, i) => {
              const day = l.ts ? new Date(l.ts).toLocaleDateString() : null;
              const prevDay = i > 0 && log[i - 1].ts ? new Date(log[i - 1].ts).toLocaleDateString() : null;
              const showDay = day && day !== prevDay && day !== new Date().toLocaleDateString();
              return (
                <React.Fragment key={i}>
                  {showDay && (
                    <div className="flex justify-center">
                      <span className="text-[10px] text-muted-foreground bg-muted/70 rounded-full px-2 py-0.5" data-testid="buddy-day-sep">{day}</span>
                    </div>
                  )}
                  <div className={`flex ${l.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div
                      data-testid={l.role === "user" ? "buddy-log-user" : "buddy-log-reply"}
                      className={`max-w-[85%] rounded-2xl px-3 py-1.5 text-[13px] leading-snug
                        ${l.role === "user" ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-muted rounded-bl-sm"}`}
                    >
                      {l.text}
                      {l.role !== "user" && l.text && user?.id && (
                        <div className="mt-1 flex justify-end" data-testid="buddy-log-replay">
                          <BuddySpeakButton text={l.text} userId={user.id} />
                        </div>
                      )}
                      {l.job && (
                        <button
                          data-testid="buddy-open-job"
                          onClick={() => { setOpen(false); navigate(`/jobs/${l.job.id}`); }}
                          className="mt-1.5 flex items-center gap-1 text-[11px] font-semibold text-primary underline underline-offset-2"
                        >
                          {t("buddy_open_job")} {l.job.label}
                        </button>
                      )}
                    </div>
                  </div>
                </React.Fragment>
              );
            })}

            {/* voice-guided measurement session */}
            {measure && (
              <BuddyMeasureCard
                session={measure} t={t} busy={measureBusy}
                onFinish={() => measureOp("finish")}
                onCancel={() => measureOp("cancel")}
                onPhoto={(n) => takeUnitPhoto(measure.id, n)}
                onDismiss={() => setMeasure(null)}
                onOpenNotes={() => { setOpen(false); if (measure.notes_conversation_id) navigate(`/chat/${measure.notes_conversation_id}`); }}
              />
            )}
            {trim && (
              <BuddyTrimCard
                session={trim} t={t} busy={trimBusy}
                onFinish={() => trimOp("finish")}
                onCancel={() => trimOp("cancel")}
                onDismiss={() => setTrim(null)}
                onOpenNotes={() => { setOpen(false); if (trim.notes_conversation_id) navigate(`/chat/${trim.notes_conversation_id}`); }}
              />
            )}
            <input ref={measurePhotoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onMeasurePhotoFile} data-testid="buddy-measure-photo-input" />
            {/* draft confirmation card (message / group / task / issue) */}
            {draft && (
              <div data-testid="buddy-draft-card" className="rounded-xl border-2 border-amber-400/60 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 space-y-1.5">
                <div className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide" data-testid="buddy-draft-kind">
                  {draft.type === "draft_message" && `${t("buddy_to")}: ${draft.recipient_name}`}
                  {draft.type === "draft_group_message" && `${t("buddy_group")}: ${draft.group_name}`}
                  {draft.type === "draft_task" && `${t("buddy_new_task")} \u00b7 ${draft.job_label || ""}`}
                  {draft.type === "draft_issue" && `${t("buddy_new_issue")} \u00b7 ${draft.job_label || ""}`}
                  {draft.type === "draft_memory" && t("buddy_new_memory")}
                  {draft.type === "draft_knowledge" && (draft.op === "delete" ? t("buddy_know_delete") : t("buddy_know_update"))}
                  {draft.type === "draft_intro" && t("buddy_intro_draft")}
                  {draft.type === "draft_delete_conversation" && t("buddy_delete_conv")}
                  {draft.type === "draft_group_create" && t("buddy_new_group")}
                  {draft.type === "draft_forward" && `${t("buddy_send_media")} \u2192 ${draft.to_label || ""}`}
                  {draft.type === "draft_edit_message" && `${t("buddy_edit_msg")} \u00b7 ${draft.conversation || ""}`}
                  {draft.type === "draft_delete_message" && `${t("buddy_delete_msg")} \u00b7 ${draft.conversation || ""}`}
                  {draft.type === "draft_issue_status" && t("buddy_issue_status")}
                  {draft.type === "draft_task_done" && (draft.done === false ? t("buddy_task_reopen") : t("buddy_task_complete"))}
                  {draft.type === "draft_account_decision" && (draft.action === "reject" ? t("buddy_admin_reject") : t("buddy_admin_approve"))}
                  {draft.type === "draft_broadcast" && `${t("buddy_admin_broadcast")} \u2192 ${draft.audience_label || ""} (${draft.audience_count ?? 0})`}
                  {draft.type === "draft_user_update" && t("buddy_admin_user_update")}
                  {draft.type === "draft_user_delete" && t("buddy_admin_user_delete")}
                  {draft.type === "draft_company_decision" && (draft.kind === "remove_member" ? t("buddy_company_remove") : draft.action === "reject" ? (draft.kind === "leave" ? t("buddy_company_leave_reject") : t("buddy_admin_reject")) : (draft.kind === "leave" ? t("buddy_company_leave_approve") : t("buddy_admin_approve")))}
                  {draft.type === "draft_measure_send" && `${t("buddy_measure_send")} \u2192 ${draft.label || ""}`}
                  {draft.type === "draft_trim_send" && `${t("buddy_trim_send")} \u2192 ${draft.label || ""}`}
                </div>
                {draft.type === "draft_company_decision" && (
                  <div className="text-[11px] text-muted-foreground" data-testid="buddy-draft-company-decision"><span className="font-medium text-foreground">{draft.title}</span></div>
                )}
                {(draft.type === "draft_account_decision" || draft.type === "draft_user_update" || draft.type === "draft_user_delete") && (
                  <div className="text-[11px] text-muted-foreground" data-testid={`buddy-draft-${draft.type.replace("draft_", "").replace("_", "-")}`}>
                    <span className="font-medium text-foreground">{draft.title}</span>
                    {draft.type === "draft_user_update" && draft.changes && (
                      <span>{" \u2192 "}{Object.entries(draft.changes).map(([k, v]) => `${k === "role" ? t(String(v)) : k === "buddy_enabled" ? `Buddy ${v ? "on" : "off"}` : `${k}: ${v}`}`).join(", ")}</span>
                    )}
                  </div>
                )}
                {draft.type === "draft_broadcast" && (
                  <div className="text-[11px] text-muted-foreground" data-testid="buddy-draft-broadcast">
                    <span className="font-medium text-foreground">{draft.title}</span>{draft.body ? ` \u2014 ${draft.body}` : ""}
                    {draft.times > 1 ? ` \u00b7 ${draft.times}\u00d7 / ${draft.interval_hours}h` : ""}
                  </div>
                )}
                {(draft.type === "draft_forward" || draft.type === "draft_delete_message") && (
                  <div className="text-[11px] text-muted-foreground" data-testid={`buddy-draft-${draft.type === "draft_forward" ? "media" : "delete-msg"}`}>{draft.title}</div>
                )}
                {draft.type === "draft_edit_message" && (
                  <div className="text-[11px] text-muted-foreground" data-testid="buddy-draft-edit-msg"><s>{draft.title}</s> \u2192 <span className="text-foreground">{draft.text}</span></div>
                )}
                {(draft.type === "draft_issue_status" || draft.type === "draft_task_done") && (
                  <div className="text-[11px] text-muted-foreground" data-testid={`buddy-draft-${draft.type === "draft_issue_status" ? "issue" : "task"}`}>
                    <span className="font-medium text-foreground">{draft.title}</span>
                    {draft.job ? ` \u00b7 ${draft.job}` : ""}
                    {draft.type === "draft_issue_status" ? ` \u2192 ${t(`status_${draft.status}`)}` : ""}
                  </div>
                )}
                {draft.type === "draft_group_create" && (
                  <div className="text-[11px] text-muted-foreground" data-testid="buddy-draft-group">
                    <span className="font-medium text-foreground">{draft.name}</span> · {(draft.member_names || []).join(", ")}
                  </div>
                )}
                {draft.type === "draft_task" && (
                  <div className="text-[11px] text-muted-foreground">
                    {t("buddy_to")}: <span className="font-medium text-foreground">{draft.assignee_name}</span>
                    {draft.due_label ? ` \u00b7 ${draft.due_label}` : ""}
                  </div>
                )}
                {draft.type === "draft_issue" && (
                  <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                    <span className={`h-2 w-2 rounded-full ${draft.priority === "high" ? "bg-destructive" : draft.priority === "low" ? "bg-muted-foreground/50" : "bg-amber-500"}`} />
                    {draft.priority}
                  </div>
                )}
                {draft.type === "draft_knowledge" && draft.op === "update" && draft.old_text && (
                  <div className="text-[12px] text-muted-foreground line-through">{draft.old_text}</div>
                )}
                <div className="text-[13px]">{draft.text || draft.title || draft.fact || (draft.type === "draft_intro" ? t("buddy_intro_cleared") : "")}</div>
                {draft.description && draft.description !== draft.title && (
                  <div className="text-[12px] text-muted-foreground">{draft.description}</div>
                )}
                <div className="flex gap-1.5 pt-0.5">
                  <Button
                    data-testid="buddy-draft-cancel"
                    size="sm" variant="outline" className="h-8 text-xs flex-1"
                    onClick={() => setDraft(null)}
                  >
                    {t("buddy_cancel")}
                  </Button>
                  <Button
                    data-testid="buddy-draft-send"
                    size="sm" className="h-8 text-xs flex-1"
                    disabled={sendingDraft}
                    onClick={confirmDraft}
                  >
                    {draft.type === "draft_memory"
                      ? <Brain className="h-3.5 w-3.5 mr-1" />
                      : <Send className="h-3.5 w-3.5 mr-1" />}
                    {sendingDraft ? "..."
                      : draft.type === "draft_memory" ? t("buddy_remember_btn")
                      : (draft.type === "draft_task" || draft.type === "draft_issue" || draft.type === "draft_knowledge" || draft.type === "draft_intro" || draft.type === "draft_delete_conversation" || draft.type === "draft_group_create" || draft.type === "draft_issue_status" || draft.type === "draft_task_done" || draft.type === "draft_edit_message" || draft.type === "draft_delete_message" || draft.type === "draft_account_decision" || draft.type === "draft_broadcast" || draft.type === "draft_user_update" || draft.type === "draft_user_delete" || draft.type === "draft_company_decision" ? t("buddy_confirm") : t("buddy_send"))}
                  </Button>
                </div>
              </div>
            )}
            <div ref={logEndRef} />
          </div>

          {/* typed command (same brain as voice) */}
          <form
            className="border-t px-3 pt-2 flex items-center gap-2 shrink-0"
            onSubmit={(e) => {
              e.preventDefault();
              const txt = typedCmd.trim();
              if (!txt || mood === "thinking") return;
              setTypedCmd("");
              if (moodRef.current === "talking") stopSpeaking();
              runAskRef.current?.({ text: txt });
            }}
          >
            <input
              data-testid="buddy-text-input"
              value={typedCmd}
              onChange={(e) => setTypedCmd(e.target.value)}
              placeholder={t("buddy_type_ph")}
              maxLength={600}
              className="flex-1 h-9 rounded-full border bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            <Button type="submit" size="icon" className="h-9 w-9 rounded-full shrink-0" data-testid="buddy-text-send"
              disabled={!typedCmd.trim() || mood === "thinking"} aria-label="send">
              <Send className="h-4 w-4" />
            </Button>
          </form>
          {/* hold-to-talk */}
          <div className="px-3 py-3 flex flex-col items-center gap-1.5 shrink-0 select-none" style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }}>
            <button
              data-testid="buddy-mic-btn"
              onPointerDown={startRecording}
              onPointerUp={stopRecording}
              onPointerCancel={stopRecording}
              onContextMenu={(e) => e.preventDefault()}
              disabled={mood === "thinking"}
              style={{ touchAction: "none", WebkitTouchCallout: "none", WebkitUserSelect: "none", userSelect: "none" }}
              className={`h-16 w-16 rounded-full flex items-center justify-center text-white shadow-lg select-none
                transition-transform duration-150 disabled:opacity-60
                ${mood === "listening" ? "bg-destructive scale-110" : "bg-primary hover:opacity-90"}`}
            >
              <Mic className="h-7 w-7" />
            </button>
            <div className="text-[11px] text-muted-foreground" data-testid="buddy-mic-hint">
              {handsFree === "waiting" ? `${t("buddy_speak_now")} · ${t("buddy_tap_cancel")}`
                : handsFree === "recording" ? `${t("buddy_handsfree_hint")} · ${t("buddy_tap_cancel")}`
                : wakeAwake && mood === "idle" ? t("buddy_listening")
                : mood === "listening" ? t("buddy_listening")
                : mood === "thinking" ? t("buddy_thinking")
                : mood === "talking" && wakeEnabled && bargeInCapable() ? `${t("buddy_speaking")} · ${t("buddy_interrupt_hint")}`
                : mood === "talking" ? t("buddy_speaking")
                : wakeEnabled ? `${t("buddy_hold")} · ${t("buddy_wake_hint")}` : t("buddy_hold")}
            </div>
          </div>
          </>
          )}
        </div>
      )}
    </>
  );
};
